import React from 'react';
import type { Order } from '../../types';
import { orderCreatedAtMs } from '../../lib/orderTimestamps';

function waitstaffOrderTitle(order: Order): string {
  if (order.orderType === 'takeaway') {
    return `Takeaway${order.customerNumber != null ? ` #${order.customerNumber}` : ''}`;
  }
  if (order.orderType === 'delivery') return 'Delivery';
  if (order.orderType === 'uber-doordash') {
    return order.deliveryInfo?.platform || order.customerName || 'Uber / DoorDash';
  }
  return order.tableName || `Table ${order.tableId}`;
}

function waitstaffTypeLabel(order: Order): string {
  if (order.orderType === 'uber-doordash') return 'Uber/DD';
  if (order.orderType === 'delivery') return 'Delivery';
  if (order.orderType === 'takeaway') return 'Takeaway';
  if (order.orderType === 'dine-in') return 'Dine-in';
  return order.orderType;
}

export { waitstaffOrderTitle, waitstaffTypeLabel };

interface WaitstaffTicketCardProps {
  order: Order;
  onOpen?: () => void;
  secondaryLabel?: string;
  actions?: React.ReactNode;
}

const WaitstaffTicketCard: React.FC<WaitstaffTicketCardProps> = ({ order, onOpen, secondaryLabel, actions }) => {
  const visibleItems = (order.items ?? []).filter((i) => !i.removed);
  const itemCount = visibleItems.reduce((sum, i) => sum + i.quantity, 0);
  const preview = visibleItems.slice(0, 3).map((i) => `${i.quantity}× ${i.name}`).join(' · ');
  const more = visibleItems.length > 3 ? ` +${visibleItems.length - 3} more` : '';
  const elapsedMs = Date.now() - orderCreatedAtMs(order);
  const elapsedMins = Math.floor(elapsedMs / 60000);
  const elapsedText =
    elapsedMins < 1 ? 'Just now' : elapsedMins === 1 ? '1 min ago' : `${elapsedMins} min ago`;
  const isTakeaway = order.orderType === 'takeaway';
  return (
    <div className="w-full text-left rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <button type="button" onClick={onOpen} className="w-full text-left active:scale-[0.99] transition-transform">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span
                className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  isTakeaway ? 'bg-gray-900 text-white' : 'bg-pink-100 text-pink-700'
                }`}
              >
                {waitstaffTypeLabel(order)}
              </span>
              <span className="text-[11px] text-gray-400">{elapsedText}</span>
            </div>
            <p className="text-base font-bold text-gray-900 truncate">{waitstaffOrderTitle(order)}</p>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
              {preview || 'No items'}
              {more}
            </p>
            {secondaryLabel ? <p className="text-[11px] text-gray-400 mt-1">{secondaryLabel}</p> : null}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-base font-bold text-gray-900 tabular-nums">${(order.totalAmount ?? 0).toFixed(2)}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{itemCount} items</p>
          </div>
        </div>
      </button>
      {actions ? <div className="mt-3 flex gap-2">{actions}</div> : null}
    </div>
  );
};

export default WaitstaffTicketCard;
